import {
  BottomSheet,
  DialogFooter,
  SystemInlineBanner,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Link, Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../../components/BottomSheetHeader";
import { Button } from "../../../components/Button";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { useWorkplaceModalsDataContext } from "../../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { MyShiftCard } from "../../Card/MyShiftCard";

export interface BundleBookedSheetProps {
  modalState: UseModalState;
}

export function BundleBookedSheet(props: BundleBookedSheetProps) {
  const { modalState } = props;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const { shift, bundle, bundleShiftPays, bundleBookingResults } = useShiftModalsDataContext();

  const workplace = shift?.workplace;
  const workplaceTimezone = workplace?.attributes?.location?.timezone;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace?.id,
  });

  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels(
    isDefined(bundle) ? [bundle.qualification] : []
  );
  const [qualificationText] = qualificationDisplayLabels;

  const bookedShiftIds = bundleBookingResults?.bookedShiftIds ?? [];
  const failedShiftIds = bundleBookingResults?.failedShiftIds ?? [];

  const bookedShifts = (bundle?.shifts ?? []).filter((bundleShift) =>
    bookedShiftIds.includes(bundleShift.id)
  );

  const bookedCount = bookedShifts.length;
  const failedCount = failedShiftIds.length;

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onClose={() => {
            modalState.closeModal();
          }}
        />
      }
      footer={
        isDefined(workplace) ? (
          <DialogFooter orientation="vertical" LinkComponent={Link}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                const modalPath = generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                  workplaceId: workplace.id,
                });
                modalState.closeModal();
                history.push(modalPath);
              }}
            >
              See workplace profile
            </Button>
          </DialogFooter>
        ) : undefined
      }
    >
      <DialogContent sx={{ backgroundColor: "unset" }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          <Title variant="h2" component="h2" sx={{ textAlign: "center" }}>
            {bookedCount} {pluralize("shift", bookedCount)} booked!
          </Title>

          {failedCount > 0 && (
            <SystemInlineBanner iconType="triangle-exclamation" color="warning">
              <Text inline variant="body2" color="inherit">
                {failedCount} other selected {pluralize("shift", failedCount)} couldn&apos;t be
                confirmed
              </Text>
            </SystemInlineBanner>
          )}

          <Stack spacing={5} sx={{ width: "100%" }}>
            {bookedShifts.map((bookedShift) => {
              const shiftPay = bundleShiftPays?.get(bookedShift.id);
              const totalPayInDollars = isDefined(shiftPay)
                ? shiftPay.totalPayInMinorUnits / 100
                : undefined;

              return (
                <MyShiftCard
                  key={bookedShift.id}
                  outlined
                  shiftStart={bookedShift.attributes.start}
                  shiftEnd={bookedShift.attributes.end}
                  shiftTimeSlot={bookedShift.attributes.timeSlot}
                  workplaceName={workplace?.attributes?.name}
                  workplaceTimezone={workplaceTimezone}
                  shiftFormattedTotalPay={
                    isDefined(shiftPay)
                      ? formatCentsAsUsd(shiftPay.totalPayInMinorUnits)
                      : undefined
                  }
                  shiftTotalPay={totalPayInDollars}
                  shiftHourlyPay={
                    isDefined(shiftPay)
                      ? formatCentsAsUsd(shiftPay.hourlyPayInMinorUnits)
                      : undefined
                  }
                  shiftBoost={undefined}
                  requiresBreak={workplace?.attributes?.requiresLunchBreak}
                  breakDurationInMinutes={mandatoryBreakDurationInMinutes}
                  qualificationText={qualificationText ?? bundle?.qualification ?? ""}
                />
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
